import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  FileText, 
  Sparkles,
  Check,
  Copy,
  Database,
  Globe,
  Key,
  Cloud,
  Download,
  Layers,
  Plus,
  Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  resourcesData, 
  EmergentCTA,
  AboutEmergent,
  RelatedResources, 
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';
import { EditableText, useAdmin } from '../../AdminContentEditor';

const resource = resourcesData['content-management'];

/* ═══════════════════════════════════════════
   PROMPTS — New Project (3 prompts)
   ═══════════════════════════════════════════ */

const PROMPT_NEW_1 = `Build a content management system for my website.

BACKEND (server.py):
- Add these endpoints:
  GET  /api/admin/sections/{section_id}  — public, returns saved content for that section (no auth)
  POST /api/admin/sections               — admin only, saves content (requires auth)
  POST /api/admin/upload                 — admin only, uploads image file to Cloudinary and returns the URL
  GET  /api/admin/media-library          — public, lists all previously uploaded Cloudinary images

- Admin authentication (pick one or both):

  OPTION A — Google Auth (recommended for production):
  Use Emergent's Google OAuth. Only allow these admin emails: [YOUR_EMAIL@gmail.com]
  Store approved admin emails in MongoDB collection "admin_emails".
  
  OPTION B — Magic Link (quick dev/testing shortcut):
  POST /api/auth/magic — accepts "key" param. If key matches env var ADMIN_MAGIC_KEY, create a session token and return it. Fast for solo dev, but use a long random key and don't share the URL.

- MongoDB collection "admin_sections" stores content as: { section_id, field_name: value, updated_at }
- Cloudinary config reads from .env: CLOUDINARY_CLOUD_NAME, CLOUDINARY_API_KEY, CLOUDINARY_API_SECRET
- If using magic link: add ADMIN_MAGIC_KEY to .env (use a long random string)

- CORS: allow all origins
- Every GET is public (no auth). Every POST checks for a valid admin session.`;

const PROMPT_NEW_2 = `Now build the frontend inline editor components.

Create an AdminContentEditor.jsx with these exports:

1. AdminProvider (context wrapper)
   - On mount: checks if user is authenticated (Google session or localStorage token)
   - If authenticated and email is in admin_emails collection: sets isAdmin = true
   - Loads ALL section content from GET /api/admin/sections on mount
   - Provides: { isAdmin, isEditMode, toggleEditMode, sectionContent, updateSectionContent }
   - Wrap your entire App with <AdminProvider>

2. EditableText component
   Props: sectionId, field, defaultValue, as (h1/p/span), className
   - Non-admin: renders the saved text (or defaultValue) as a normal element
   - Admin in edit mode: element becomes contentEditable, blue dashed border appears
   - On blur: auto-saves to POST /api/admin/sections

3. EditableImage component
   Props: sectionId, field, defaultSrc, alt, className
   - Non-admin: renders normal <img>
   - Admin in edit mode: purple overlay on hover with camera icon
   - Click opens a modal with two tabs: "Upload New" and "Media Library"
   - Upload New: file picker → compress image client-side (max 1920px, 80% JPEG quality using Canvas) → POST /api/admin/upload → saves returned URL
   - Media Library: fetches GET /api/admin/media-library → grid of previously uploaded images → click to select
   - Client-side compression is critical — deployment proxies reject files over ~1MB

4. EditModeToggle component
   - Floating button (bottom-right) only visible to admins
   - Toggles between view mode and edit mode
   - Shows "Editing" state with green indicator

5. Login page
   - If using Google Auth: button that triggers Emergent Google OAuth flow
   - If using magic link: page at /auth/magic that reads "key" from URL params, sends POST /api/auth/magic, stores token in localStorage on success
   - After login: redirect to homepage, admin can toggle edit mode`;

const PROMPT_NEW_3 = `Now connect everything and add inline editing to my homepage.

1. In App.js:
   - Wrap everything with <AdminProvider>
   - Add <EditModeToggle /> inside the provider (renders globally)
   - Add login route (Google Auth callback or /auth/magic for magic link)

2. On the homepage (or any page), replace static text and images:

   BEFORE: <h1>Welcome to My Site</h1>
   AFTER:  <h1><EditableText sectionId="homepage" field="title" defaultValue="Welcome to My Site" as="span" /></h1>

   BEFORE: <img src="/hero.jpg" alt="Hero" />
   AFTER:  <EditableImage sectionId="homepage" field="hero" defaultSrc="/hero.jpg" alt="Hero" className="w-full h-full object-cover" />

3. That's it. Every EditableText and EditableImage:
   - Shows saved content to everyone (loads from DB on page load)
   - Becomes editable when an admin toggles edit mode
   - Auto-saves changes to MongoDB
   - Images persist on Cloudinary (survive deployments)

4. To log in as admin:
   - Google Auth: Visit the login page and sign in with your approved Google account
   - Magic Link (dev only): Visit https://your-site.com/auth/magic?key=YOUR_KEY
   Then toggle edit mode with the floating button — click any text or image to edit.`;

/* ═══════════════════════════════════════════
   PROMPTS — Existing Project (2 prompts)
   ═══════════════════════════════════════════ */

const PROMPT_EXISTING_1 = `Add a content management system to my EXISTING website. Don't replace anything — add alongside what I already have.

BACKEND — Add these endpoints to my existing server.py:

1. Content endpoints:
   GET  /api/admin/sections/{section_id}  — public, returns saved content (no auth)
   POST /api/admin/sections               — admin only, saves content (requires auth)
   POST /api/admin/upload                 — admin only, uploads image to Cloudinary, returns URL
   GET  /api/admin/media-library          — public, lists all previously uploaded images

2. Admin authentication (pick one or both):
   
   OPTION A — Google Auth (recommended):
   Use Emergent's Google OAuth. Only allow these admin emails: [YOUR_EMAIL@gmail.com]
   Store approved emails in MongoDB "admin_emails" collection.
   
   OPTION B — Magic Link (dev shortcut only):
   POST /api/auth/magic — accepts "key" param. If key matches ADMIN_MAGIC_KEY env var, creates a session token.
   Use a long random string as the key. Not recommended for production.

3. Auth middleware:
   Create a require_admin dependency that checks the session for a valid admin.

4. MongoDB:
   Use the existing db connection. Create an "admin_sections" collection.
   Schema: { section_id, field1: value1, field2: value2, ..., updated_at }

5. Cloudinary:
   Read CLOUDINARY_CLOUD_NAME, CLOUDINARY_API_KEY, CLOUDINARY_API_SECRET from .env
   Upload to a folder called "admin_uploads"

Keep all my existing endpoints, routes, and logic exactly as they are. Just add the CMS endpoints.`;

const PROMPT_EXISTING_2 = `Now add inline editing components to my EXISTING frontend. Don't replace my current components — add new ones alongside them.

1. Create a new file: AdminContentEditor.jsx with these exports:
   - AdminProvider: context wrapper that loads content from GET /api/admin/sections on mount (for ALL visitors), checks for admin session (Google Auth or magic link token), provides { isAdmin, isEditMode, sectionContent, updateSectionContent }
   - EditableText: drop-in text component. Props: sectionId, field, defaultValue, as, className. Public: shows text. Admin edit mode: contentEditable with auto-save on blur.
   - EditableImage: drop-in image component. Props: sectionId, field, defaultSrc, alt, className. Public: shows image. Admin edit mode: hover overlay → click → modal with Upload New + Media Library tabs. Client-side compression before upload (max 1920px, 80% JPEG).
   - EditModeToggle: floating button (bottom-right), only visible to admins.
   - useAdmin: hook to access admin context.

2. Create a login page:
   - Google Auth: Login button that triggers OAuth flow (recommended)
   - Magic Link (dev only): page at /auth/magic that reads "key" from URL params, POSTs to /api/auth/magic, stores token on success

3. In my existing App.js:
   - Wrap the existing content with <AdminProvider>
   - Add <EditModeToggle /> inside the provider
   - Add login route

4. NOW — to make any element on my existing pages editable, I just swap:
   <h1>My Title</h1>  →  <h1><EditableText sectionId="page-section" field="title" defaultValue="My Title" as="span" /></h1>
   <img src="..." />  →  <EditableImage sectionId="page-section" field="hero" defaultSrc="..." className="..." />

5. I can do this incrementally — one page at a time, one element at a time. No need to convert everything at once.`;

/* ═══════════════════════════════════════════
   HELPER COMPONENTS
   ═══════════════════════════════════════════ */

const PromptCard = ({ number, title, description, prompt, color }) => {
  const [copied, setCopied] = useState(false);
  const colorMap = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    emerald: 'bg-emerald-500'
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-8" data-testid={`resource-prompt-${number}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg ${colorMap[color] || 'bg-gray-500'} flex items-center justify-center`}>
          <span className="text-white font-bold text-sm">{number}</span>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <p className="text-gray-500 text-sm">{description}</p>
        </div>
      </div>
      <div className="relative bg-gray-900 rounded-xl p-5 overflow-hidden">
        <div className={`absolute top-0 left-0 w-1 h-full ${colorMap[color] || 'bg-gray-500'}`} />
        <button
          onClick={handleCopy}
          className="absolute top-3 right-3 p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10 z-10"
          data-testid={`copy-resource-prompt-${number}`}
        >
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
        <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono pr-10 leading-relaxed">{prompt}</pre>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════ */

const ContentManagementPage = () => {
  const { isAdmin, isEditMode } = useAdmin();
  const [activePath, setActivePath] = useState('new');

  const allPromptsNew = `${PROMPT_NEW_1}\n\n---\n\n${PROMPT_NEW_2}\n\n---\n\n${PROMPT_NEW_3}`;
  const allPromptsExisting = `${PROMPT_EXISTING_1}\n\n---\n\n${PROMPT_EXISTING_2}`;

  const [copiedAll, setCopiedAll] = useState(false);
  const handleCopyAll = () => {
    const text = activePath === 'new' ? allPromptsNew : allPromptsExisting;
    navigator.clipboard.writeText(text);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 pt-20 pb-12">
          <Link to="/resources" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 text-sm" data-testid="back-to-resources">
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <EditableText sectionId="res-cm-hero" field="title" defaultValue="Inline Content Management" as="span" />
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl">
            <EditableText sectionId="res-cm-hero" field="description" defaultValue="Click, type, save. Edit your live site directly — no admin panel, no CMS dashboard. Everything you need to set it up." as="span" />
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCopyAll}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                copiedAll
                  ? 'bg-green-500 text-white'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
              }`}
              data-testid="copy-all-prompts-btn"
            >
              {copiedAll ? <><Check className="w-5 h-5" /> Copied All Prompts!</> : <><Copy className="w-5 h-5" /> Copy All Prompts</>}
            </button>
            <a
              href="/inline-content-management-guide.md"
              download="inline-content-management-guide.md"
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors"
              data-testid="download-guide-btn"
            >
              <Download className="w-5 h-5" />
              Download Guide (.md)
            </a>
            
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* ─── STEP 0: CLOUDINARY SETUP ─── */}
        <section className="mb-12">
          <div className="p-6 rounded-2xl bg-gray-900 text-white">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Cloud className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold">First: Set Up Cloudinary (2 min)</h2>
                <p className="text-gray-400 text-sm">Required for both paths. Images need to survive deployments.</p>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              {[
                'Go to cloudinary.com → sign up (free, 25GB)',
                'Dashboard → copy: Cloud Name, API Key, API Secret',
                'Add to your backend .env (or Emergent Secrets for deployed sites)'
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 text-xs font-bold">{i + 1}</span>
                  </div>
                  <p className="text-gray-300 text-sm">{step}</p>
                </div>
              ))}
            </div>
            <div className="p-4 rounded-xl bg-black/30 font-mono text-sm text-gray-400">
              <p className="text-emerald-400 mb-1"># Add to backend .env</p>
              <p>CLOUDINARY_CLOUD_NAME=your_cloud_name</p>
              <p>CLOUDINARY_API_KEY=your_api_key</p>
              <p>CLOUDINARY_API_SECRET=your_api_secret</p>
            </div>
            <p className="text-gray-500 text-xs mt-3">Admin auth is set up separately — see the authentication section below for Google Auth (recommended) or magic link options.</p>
          </div>
        </section>

        {/* ─── PATH SELECTOR ─── */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Pick Your Path</h2>
          <p className="text-gray-500 mb-6">Same result, different starting points.</p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => setActivePath('new')}
              className={`p-6 rounded-2xl border-2 text-left transition-all ${
                activePath === 'new'
                  ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              data-testid="path-new-project"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activePath === 'new' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <Plus className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">New Project</h3>
              </div>
              <p className="text-sm text-gray-600">Starting from scratch on Emergent. 3 prompts build everything.</p>
              <p className="text-xs text-gray-400 mt-2">Backend + Auth + Frontend + Wiring</p>
            </button>

            <button
              onClick={() => setActivePath('existing')}
              className={`p-6 rounded-2xl border-2 text-left transition-all ${
                activePath === 'existing'
                  ? 'border-purple-500 bg-purple-50 shadow-lg shadow-purple-100'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              data-testid="path-existing-project"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activePath === 'existing' ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  <Settings className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Existing Project</h3>
              </div>
              <p className="text-sm text-gray-600">Already have a site? 2 prompts add CMS alongside your code.</p>
              <p className="text-xs text-gray-400 mt-2">Adds endpoints + components without touching existing code</p>
            </button>
          </div>
        </section>

        {/* ─── PROMPTS ─── */}
        <section className="mb-12">
          {activePath === 'new' ? (
            <>
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-emerald-500" />
                <h2 className="text-2xl font-bold text-gray-900">New Project: 3 Prompts</h2>
              </div>
              <p className="text-gray-500 mb-8">Paste each prompt into Emergent in order. Wait for each to finish before pasting the next.</p>

              <PromptCard number="1" title="Backend + Auth + Cloudinary" description="Builds the entire server: content API, image uploads, magic link login." prompt={PROMPT_NEW_1} color="blue" />
              <PromptCard number="2" title="Frontend Inline Editor" description="Creates AdminProvider, EditableText, EditableImage, and the login page." prompt={PROMPT_NEW_2} color="purple" />
              <PromptCard number="3" title="Connect + Start Editing" description="Wires it all together. Shows how to make any element editable." prompt={PROMPT_NEW_3} color="emerald" />
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-6">
                <Settings className="w-5 h-5 text-purple-500" />
                <h2 className="text-2xl font-bold text-gray-900">Existing Project: 2 Prompts</h2>
              </div>
              <p className="text-gray-500 mb-8">These add CMS endpoints and components alongside your existing code. Nothing gets replaced.</p>

              <PromptCard number="1" title="Add CMS Backend" description="Adds content endpoints, image upload, and admin auth to your existing server.py." prompt={PROMPT_EXISTING_1} color="blue" />
              <PromptCard number="2" title="Add Inline Editor Frontend" description="Creates the editor components and wires them into your existing app." prompt={PROMPT_EXISTING_2} color="purple" />
            </>
          )}
        </section>

        {/* ─── HOW TO LOG IN ─── */}
        <section className="mb-12">
          <div className="p-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
            <div className="flex items-center gap-3 mb-4">
              <Key className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold text-gray-900">Admin Authentication</h3>
            </div>
            <div className="space-y-4">
              {/* Recommended: Google Auth */}
              <div className="p-4 rounded-xl bg-white border border-emerald-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">RECOMMENDED</span>
                  <span className="font-semibold text-gray-900">Emergent Google Auth</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">Secure OAuth login — no secrets to manage, no keys to leak. Only approved Google accounts can access edit mode.</p>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><strong>1.</strong> In your Emergent prompt, ask for <em>"Google Auth login — only allow these emails to access admin: your@email.com"</em></p>
                  <p><strong>2.</strong> Emergent handles the OAuth setup automatically</p>
                  <p><strong>3.</strong> Admins log in with their Google account — no passwords, no keys</p>
                </div>
              </div>

              {/* Development only: Magic Link */}
              <div className="p-4 rounded-xl bg-white border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">DEV / TESTING ONLY</span>
                  <span className="font-semibold text-gray-900">Magic Link</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">Quick shortcut for development. A secret URL grants admin access — useful for solo testing but <strong>not recommended for production</strong> or shared teams.</p>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><strong>1.</strong> Set <code className="px-1.5 py-0.5 rounded bg-gray-100 border text-gray-800">ADMIN_MAGIC_KEY=a_long_random_string</code> in your backend .env</p>
                  <p><strong>2.</strong> Visit: <code className="px-1.5 py-0.5 rounded bg-gray-100 border text-gray-800">https://your-site.com/auth/magic?key=a_long_random_string</code></p>
                  <p><strong>3.</strong> Token saved in browser — edit mode available</p>
                </div>
                <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-xs text-red-700"><strong>Security note:</strong> Anyone with the key URL can become admin. Use a long random string (not "admin" or "test"). Never share the URL publicly. For real projects with teams, use Google Auth instead.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── TROUBLESHOOTING ─── */}
        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Troubleshooting</h3>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 w-1/3">Problem</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Fix</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ['Edits not visible to public', 'GET /api/admin/sections must NOT require auth. It\'s public — loads for everyone.'],
                  ['401 when saving', 'Re-login via magic link. Check auth_session_token exists in localStorage.'],
                  ['Upload returns 500', 'Image too large for proxy. Client-side compression should handle this — check browser console.'],
                  ['Images gone after deploy', 'Not using Cloudinary. Server disk is wiped each deploy. Cloudinary persists images.'],
                  ['CORS errors', 'Use window.location.origin for API URL. Set CORS_ORIGINS="*" in backend .env.'],
                  ['GIFs not animating', 'Don\'t compress GIF files — skip the Canvas compression step for image/gif content type.']
                ].map(([problem, fix], i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-red-600 font-medium">{problem}</td>
                    <td className="px-4 py-3 text-gray-600">{fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ─── QUICK REFERENCE ─── */}
        <section className="mb-12">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Reference</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-gray-900 text-gray-300">
              <p className="text-emerald-400 text-xs font-medium mb-2">EditableText</p>
              <pre className="text-xs font-mono leading-relaxed whitespace-pre-wrap">{`<EditableText
  sectionId="homepage-hero"
  field="title"
  defaultValue="Welcome!"
  className="text-4xl"
  as="h1"
/>`}</pre>
            </div>
            <div className="p-4 rounded-xl bg-gray-900 text-gray-300">
              <p className="text-purple-400 text-xs font-medium mb-2">EditableImage</p>
              <pre className="text-xs font-mono leading-relaxed whitespace-pre-wrap">{`<EditableImage
  sectionId="homepage-hero"
  field="background"
  defaultSrc="/default.jpg"
  alt="Hero background"
  className="w-full h-full object-cover"
/>`}</pre>
            </div>
          </div>
        </section>

        {/* ─── DOWNLOADS ─── */}
        <section className="mb-12">
          <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Downloads</h3>
            <div className="flex flex-wrap gap-3">
              <a href="/inline-content-management-guide.md" download className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-emerald-200 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors">
                <Download className="w-4 h-4" /> Complete Implementation Guide
              </a>
            </div>
          </div>
        </section>

        <EmergentCTA />
        <AuthorCard author={resource.author} />
        <RelatedResources resourceIds={resource.relatedResources} />
        <AboutEmergent />
      </div>
    </div>
  );
};

export default ContentManagementPage;
